import React, { useState, useEffect, useCallback } from 'react';
import { usePermissions } from '../../../hooks/usePermissions';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Snackbar,
  Tabs,
  Tab,
  LinearProgress,
  Select,
  InputLabel,
  FormControl,
  OutlinedInput,
  ListItemText,
  Checkbox,
  IconButton
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import {
  Search as SearchIcon,
  MonetizationOn as CommissionIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  TableChart as TableChartIcon,
  Assessment as AssessmentIcon,
  History as HistoryIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import axios from 'axios';

// ===== أنواع البيانات =====
interface Branch {
  _id: string;
  name: string;
}

interface InvoicePage {
  _id: string;
  name: string;
  type: string;
}

interface BranchSalesData {
  branchId: string;
  branchName: string;
  totalSales: number;
  totalReturns: number;
  netSales: number;
}

interface CommissionTarget {
  _id?: string;
  branchId: string;
  // صفحة الفاتورة المرتبطة (null = هدف إجمالي للفرع)
  invoicePageId?: string | null;
  invoicePageName?: string | null;
  periodType: 'monthly' | 'quarterly' | 'semi' | 'yearly';
  year: number;
  month?: number;
  quarter?: number;
  semi?: number;
  targetAmount: number;
  periodLabel: string;
}

interface CommissionIssue {
  _id?: string;
  branchId: string;
  branchName?: string;
  // صفحة الفاتورة المرتبطة (null/undefined = عمولة إجمالية للفرع)
  invoicePageId?: string | null;
  invoicePageName?: string | null;
  month: number;
  year: number;
  rate: number;
  value: number;
  netSales: number;
  dateFrom?: string;
  dateTo?: string;
  periodKey?: string;
  issuedAt?: string;
}

// مبيعات السنة: { branchId: { month: netSales } }
type YearSales = Record<string, Record<number, number>>;

// ===== مساعدات =====
const MONTHS_AR = [
  'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
  'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
];

const formatCurrency = (value: number): string =>
  value.toLocaleString('ar-SA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ر.س';

const formatShort = (value: number): string =>
  value.toLocaleString('ar-SA', { maximumFractionDigits: 0 });

// ===== مكوّن التبويب =====
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}
const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div hidden={value !== index} style={{ paddingTop: 16 }}>
    {value === index && children}
  </div>
);

// ===== المكون الرئيسي =====
const CommissionsReport: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const { hasPermission } = usePermissions();

  // ===== الصلاحيات =====
  const canIssueCommission = hasPermission('reports', 'sales-commissions', 'issue_commission');
  const canDeleteCommission = hasPermission('reports', 'sales-commissions', 'delete_commission');
  const canEditTargets      = hasPermission('reports', 'sales-commissions', 'edit_targets');

  // ===== الحالة العامة =====
  const [branches, setBranches] = useState<Branch[]>([]);
  const [targets,  setTargets]  = useState<CommissionTarget[]>([]);
  const [issues,   setIssues]   = useState<CommissionIssue[]>([]);
  const [error,    setError]    = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success'
  });

  // ===== صفحات الفواتير =====
  const [availableSalesPages,  setAvailableSalesPages]  = useState<InvoicePage[]>([]);
  const [availableReturnPages, setAvailableReturnPages] = useState<InvoicePage[]>([]);
  const [loadingPages,         setLoadingPages]         = useState(false);

  // صفحات الفواتير المحددة في تبويب التقرير
  const [selectedSalesPages,   setSelectedSalesPages]   = useState<string[]>([]);
  const [selectedReturnPages,  setSelectedReturnPages]  = useState<string[]>([]);

  // صفحات الفواتير المحددة في تبويب الأهداف (لحساب مبيعات السنة الماضية)
  const [selectedTargetSalesPages,  setSelectedTargetSalesPages]  = useState<string[]>([]);
  const [selectedTargetReturnPages, setSelectedTargetReturnPages] = useState<string[]>([]);

  // ===== حالة تبويب التقرير (الجدول الشهري) =====
  const [reportYear,      setReportYear]      = useState(String(new Date().getFullYear()));
  const [reportSalesData, setReportSalesData] = useState<YearSales>({});
  // مبيعات مقسمة حسب الفرع والصفحة: { "branchId_pageId": { month: netSales } }
  const [reportPageSalesData, setReportPageSalesData] = useState<Record<string, Record<number, number>>>({});
  const [loadingReport,   setLoadingReport]   = useState(false);
  const [reportBranchId,  setReportBranchId]  = useState('');

  // ===== تحديد الأشهر لإصدار العمولة =====
  const [selectedMonths,     setSelectedMonths]     = useState<number[]>([]);
  const [issueRate,          setIssueRate]          = useState('5');
  // قيم العمولة المخصصة لكل شهر (تُحسب تلقائياً وتقبل التعديل): { month: value }
  const [customCommValues,   setCustomCommValues]   = useState<Record<number, string>>({});
  const [issueModalOpen,     setIssueModalOpen]     = useState(false);
  const [currentIssueBranch, setCurrentIssueBranch] = useState<Branch | null>(null);
  const [savingIssue,        setSavingIssue]        = useState(false);
  // نوع العمولة في المودال: null = إجمالية للفرع، string = معرف صفحة محددة
  const [issuePageId,        setIssuePageId]        = useState<string | null>(null);

  // مودال حذف العمولة
  const [deleteModalOpen,   setDeleteModalOpen]   = useState(false);
  const [deletingIssue,     setDeletingIssue]     = useState<CommissionIssue | null>(null);
  const [deletingLoading,   setDeletingLoading]   = useState(false);

  // ===== حالة تبويب الأهداف =====
  const [targetsYear,     setTargetsYear]     = useState(String(new Date().getFullYear()));
  const [loadingTargets,  setLoadingTargets]  = useState(false);
  const [savingTargets,   setSavingTargets]   = useState(false);
  const [lastYearSales,     setLastYearSales]     = useState<YearSales>({});
  // مبيعات السنة الماضية مقسمة حسب الصفحة: { branchId: { pageId: { month: netSales } } }
  const [lastYearPageSales, setLastYearPageSales] = useState<Record<string, Record<string, Record<number, number>>>>({});
  const [loadingLastYear, setLoadingLastYear] = useState(false);
  // جدول التعديل: { branchId_pageId_month: value }
  const [editGrid, setEditGrid] = useState<Record<string, string>>({});
  const [editMode, setEditMode] = useState(false);
  // وضع الأهداف: 'branch' = هدف إجمالي للفرع، 'page' = هدف لكل صفحة مبيعات
  const [targetMode, setTargetMode] = useState<'branch' | 'page'>('branch');
  // الصفحات المختارة لتحديد الأهداف (في وضع 'page')
  const [selectedGoalPages, setSelectedGoalPages] = useState<string[]>([]);

  // ===== جلب البيانات عند التحميل =====
  useEffect(() => {
    fetchBranches();
    fetchInvoicePages();
  }, []);

  useEffect(() => {
    if (branches.length > 0) {
      fetchTargets();
      fetchIssues();
      fetchLastYearSales();
    }
  }, [branches]);

  useEffect(() => {
    if (branches.length > 0) {
      fetchTargets();
      fetchLastYearSales();
    }
  }, [targetsYear]);

  // إعادة جلب مبيعات السنة الماضية عند تغيير وضع الأهداف (لجلب بيانات الصفحات)
  useEffect(() => {
    if (branches.length > 0) {
      fetchLastYearSales();
    }
  }, [targetMode]);

  useEffect(() => {
    if (branches.length > 0) {
      fetchIssues();
    }
  }, [reportYear]);

  const fetchBranches = async () => {
    try {
      const response = await axios.get('/api/branches');
      setBranches(response.data.branches || []);
    } catch (err) {
      console.error('خطأ في جلب الفروع:', err);
    }
  };

  const fetchInvoicePages = async () => {
    setLoadingPages(true);
    try {
      const response = await axios.get('/api/reports/commissions/invoice-pages');
      if (response.data.success) {
        setAvailableSalesPages(response.data.salesPages || []);
        setAvailableReturnPages(response.data.returnPages || []);
      }
    } catch (err) {
      console.error('خطأ في جلب صفحات الفواتير:', err);
    } finally {
      setLoadingPages(false);
    }
  };

  const fetchTargets = async () => {
    setLoadingTargets(true);
    try {
      const response = await axios.get('/api/reports/commissions/targets', {
        params: { year: targetsYear }
      });
      if (response.data.success) {
        setTargets(response.data.data || []);
      }
    } catch (err) {
      console.error('خطأ في جلب الأهداف:', err);
    } finally {
      setLoadingTargets(false);
    }
  };

  const fetchIssues = async () => {
    try {
      const response = await axios.get('/api/reports/commissions/issues');
      if (response.data.success) {
        setIssues(response.data.data || []);
      }
    } catch (err) {
      console.error('خطأ في جلب العمولات المُصدَرة:', err);
    }
  };

  const fetchLastYearSales = async () => {
    setLoadingLastYear(true);
    try {
      const params: Record<string, string> = { year: targetsYear };
      if (selectedTargetSalesPages.length > 0)  params.salesPageIds  = selectedTargetSalesPages.join(',');
      if (selectedTargetReturnPages.length > 0) params.returnPageIds = selectedTargetReturnPages.join(',');

      // جلب إجمالي الفرع
      const response = await axios.get('/api/reports/commissions/last-year-sales', { params });
      if (response.data.success) {
        setLastYearSales(response.data.data || {});
      }

      // جلب مبيعات مقسمة حسب الصفحة (في وضع page)
      if (targetMode === 'page') {
        const pageParams: Record<string, string> = { year: targetsYear };
        if (selectedTargetSalesPages.length > 0) pageParams.salesPageIds = selectedTargetSalesPages.join(',');
        const pageResponse = await axios.get('/api/reports/commissions/last-year-sales-by-page', { params: pageParams });
        if (pageResponse.data.success) {
          setLastYearPageSales(pageResponse.data.data || {});
        }
      }
    } catch (err) {
      console.error('خطأ في جلب مبيعات السنة الماضية:', err);
    } finally {
      setLoadingLastYear(false);
    }
  };

  // ===== جلب مبيعات السنة للتبويب الأول =====
  const fetchReportSalesData = useCallback(async () => {
    setLoadingReport(true);
    setError(null);
    try {
      // جلب بيانات كل شهر (إجمالي للفرع)
      const monthlyData: YearSales = {};
      // مبيعات مقسمة حسب الصفحة: { branchId: { pageId: { month: netSales } } }
      const pageMonthlyData: Record<string, Record<string, Record<number, number>>> = {};

      const monthPromises = Array.from({ length: 12 }, async (_, mIdx) => {
        const month = mIdx + 1;
        const monthStr = String(month).padStart(2, '0');
        const lastDay = new Date(parseInt(reportYear), month, 0).getDate();
        const monthParams: Record<string, string> = {
          dateFrom: `${reportYear}-${monthStr}-01`,
          dateTo:   `${reportYear}-${monthStr}-${String(lastDay).padStart(2, '0')}`
        };
        if (reportBranchId) monthParams.branchId = reportBranchId;
        if (selectedSalesPages.length > 0)  monthParams.salesPageIds  = selectedSalesPages.join(',');
        if (selectedReturnPages.length > 0) monthParams.returnPageIds = selectedReturnPages.join(',');

        // جلب إجمالي الفرع
        const res = await axios.get('/api/reports/commissions/sales-by-branch', { params: monthParams });
        if (res.data && res.data.success) {
          const data: BranchSalesData[] = res.data.data || [];
          data.forEach(b => {
            if (!monthlyData[b.branchId]) monthlyData[b.branchId] = {};
            monthlyData[b.branchId][month] = b.netSales;
          });
        }

        // جلب مبيعات مقسمة حسب الصفحة (في وضع page)
        if (targetMode === 'page') {
          const pageRes = await axios.get('/api/reports/commissions/sales-by-branch-page', { params: monthParams });
          if (pageRes.data && pageRes.data.success) {
            const pageData: Record<string, Record<string, number>> = pageRes.data.data || {};
            Object.entries(pageData).forEach(([bId, pages]) => {
              if (!pageMonthlyData[bId]) pageMonthlyData[bId] = {};
              Object.entries(pages).forEach(([pId, netSales]) => {
                if (!pageMonthlyData[bId][pId]) pageMonthlyData[bId][pId] = {};
                pageMonthlyData[bId][pId][month] = netSales;
              });
            });
          }
        }
      });

      await Promise.all(monthPromises);
      setReportSalesData(monthlyData);
      if (targetMode === 'page') {
        const flatPageData: Record<string, Record<number, number>> = {};
        Object.entries(pageMonthlyData).forEach(([bId, pages]) => {
          Object.entries(pages).forEach(([pId, months]) => {
            flatPageData[`${bId}_${pId}`] = months;
          });
        });
        setReportPageSalesData(flatPageData);
      } else {
        setReportPageSalesData({});
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'حدث خطأ أثناء جلب البيانات');
      setReportSalesData({});
      setReportPageSalesData({});
    } finally {
      setLoadingReport(false);
    }
  }, [reportYear, reportBranchId, selectedSalesPages, selectedReturnPages, targetMode]);

  // ===== مساعدات الأهداف =====
  // جلب هدف شهري لفرع محدد (مع دعم invoicePageId اختياري)
  // pageId = null/undefined → هدف إجمالي للفرع (بدون صفحة)
  // pageId = string → هدف لصفحة محددة
  const getMonthlyTarget = (branchId: string, month: number, year: number, pageId?: string | null): CommissionTarget | undefined =>
    targets.find(t => {
      const branchMatch = t.branchId === branchId || t.branchId?.toString() === branchId;
      const periodMatch = t.periodType === 'monthly' && t.year === parseInt(year.toString()) && t.month === month;
      if (!branchMatch || !periodMatch) return false;

      if (pageId === null || pageId === undefined) {
        // نبحث عن الهدف الإجمالي (بدون صفحة)
        return !t.invoicePageId;
      } else {
        // نبحث عن هدف صفحة محددة
        return t.invoicePageId === pageId || t.invoicePageId?.toString() === pageId;
      }
    });

  // إجمالي الهدف الشهري للفرع (جمع كل الصفحات + الإجمالي)
  const getMonthlyTargetAmount = (branchId: string, month: number, year: number): number => {
    const allTargets = targets.filter(t =>
      (t.branchId === branchId || t.branchId?.toString() === branchId) &&
      t.periodType === 'monthly' &&
      t.year === parseInt(year.toString()) &&
      t.month === month
    );
    if (allTargets.length === 0) return 0;
    return allTargets.reduce((sum, t) => sum + t.targetAmount, 0);
  };

  // جلب سجل عمولة مُصدَرة لفرع وشهر وسنة
  // pageId = null/undefined → يبحث عن أي عمولة (للعرض في الجدول)
  // pageId = 'branch' → يبحث عن العمولة الإجمالية للفرع (invoicePageId=null)
  // pageId = string → يبحث عن عمولة صفحة محددة
  const getIssuedRecord = (branchId: string, month: number, year: number, pageId?: string | null): CommissionIssue | undefined => {
    const branchIssues = issues.filter(i =>
      (i.branchId === branchId || i.branchId?.toString() === branchId) &&
      i.month === month && i.year === year
    );
    if (pageId === undefined) {
      // إرجاع أول عمولة موجودة (للعرض العام في الجدول)
      return branchIssues[0];
    } else if (pageId === null) {
      // البحث عن العمولة الإجمالية للفرع (بدون صفحة)
      return branchIssues.find(i => !i.invoicePageId);
    } else {
      // البحث عن عمولة صفحة محددة
      return branchIssues.find(i => i.invoicePageId === pageId || i.invoicePageId?.toString() === pageId);
    }
  };

  // جلب جميع العمولات المُصدَرة لفرع وشهر وسنة
  const getAllIssuedRecords = (branchId: string, month: number, year: number): CommissionIssue[] =>
    issues.filter(i =>
      (i.branchId === branchId || i.branchId?.toString() === branchId) &&
      i.month === month && i.year === year
    );

  const getReportNetSales = (branchId: string, month: number): number =>
    reportSalesData[branchId]?.[month] || 0;

  // مبيعات صفحة معينة لفرع وشهر (في وضع page)
  const getReportPageNetSales = (branchId: string, pageId: string, month: number): number =>
    reportPageSalesData[`${branchId}_${pageId}`]?.[month] || 0;

  // إجمالي المردودات لفرع وشهر معين (مجموع قيم صفحات المردودات — قيم سالبة)
  const getBranchPageReturns = (branchId: string, month: number): number =>
    availableReturnPages.reduce((sum, rp) => {
      const val = reportPageSalesData[`${branchId}_${rp._id}`]?.[month] || 0;
      return sum + val; // القيم سالبة بالفعل من الـ API
    }, 0);

  // مجموع مبيعات صفحة معينة لجميع الفروع لشهر معين
  // في حالة اختيار صفحات مردودات: نخصم المردودات من مبيعات الصفحة
  const getPageTotalNetSales = (pageId: string, month: number): number => {
    const salesPages = selectedSalesPages.length > 0 ? selectedSalesPages : availableSalesPages.map(p => p._id);
    const salesTotal = displayedBranches.reduce((sum, b) => sum + getReportPageNetSales(b._id, pageId, month), 0);
    // إذا كان هناك صفحة مبيعات واحدة فقط محددة، نخصم المردودات منها
    if (salesPages.length === 1 && salesPages[0] === pageId) {
      const returnsTotal = displayedBranches.reduce((sum, b) => sum + getBranchPageReturns(b._id, month), 0);
      return salesTotal + returnsTotal; // returnsTotal سالب بالفعل
    }
    return salesTotal;
  };

  // هدف صفحة معينة (من أول فرع فقط — الهدف يُحفظ لأول فرع)
  const getPageTotalTarget = (pageId: string, month: number): number => {
    const firstBranch = displayedBranches[0] || branches[0];
    if (!firstBranch) return 0;
    return getMonthlyTarget(firstBranch._id, month, parseInt(reportYear), pageId)?.targetAmount || 0;
  };

  const getAchievementPct = (branchId: string, month: number, year: number): number | null => {
    const target = getMonthlyTargetAmount(branchId, month, year);
    if (!target) return null;
    const net = getReportNetSales(branchId, month);
    return (net / target) * 100;
  };

  // ===== مودال إصدار العمولة =====
  const openIssueModal = (branch: Branch) => {
    setCurrentIssueBranch(branch);
    setIssueRate('5');
    setSelectedMonths([]);
    setCustomCommValues({});
    setIssuePageId(null); // الافتراضي: عمولة إجمالية للفرع
    setIssueModalOpen(true);
  };

  const toggleMonth = (month: number) => {
    setSelectedMonths(prev => {
      const newSelected = prev.includes(month) ? prev.filter(m => m !== month) : [...prev, month];
      return newSelected;
    });
  };

  // حساب القيمة التلقائية للعمولة لشهر معين
  const calcAutoCommValue = (branchId: string, month: number, rate: string): number => {
    const net = getReportNetSales(branchId, month);
    return net * ((parseFloat(rate) || 0) / 100);
  };

  // الحصول على قيمة العمولة الفعلية (مخصصة أو تلقائية)
  const getEffectiveCommValue = (branchId: string, month: number): number => {
    if (customCommValues[month] !== undefined && customCommValues[month] !== '') {
      return parseFloat(customCommValues[month]) || 0;
    }
    return calcAutoCommValue(branchId, month, issueRate);
  };

  // تحديث القيم التلقائية عند تغيير النسبة
  const handleRateChange = (newRate: string) => {
    setIssueRate(newRate);
    if (!currentIssueBranch) return;
    // إعادة حساب القيم التلقائية للأشهر التي لم تُعدَّل يدوياً
    setCustomCommValues(prev => {
      const updated = { ...prev };
      // نحذف القيم التلقائية فقط (لا نمس المعدّلة يدوياً)
      // في هذا التصميم: نحذف كل القيم لإعادة الحساب التلقائي
      // المستخدم يمكنه إعادة التعديل بعد ذلك
      return {};
    });
  };

  const getTotalCommissionValue = (): number => {
    if (!currentIssueBranch) return 0;
    return selectedMonths.reduce((sum, m) => sum + getEffectiveCommValue(currentIssueBranch._id, m), 0);
  };

  const confirmIssue = async () => {
    if (!currentIssueBranch || selectedMonths.length === 0) return;
    const rate = parseFloat(issueRate) || 0;
    const year = parseInt(reportYear);
    const totalVal = getTotalCommissionValue();

    // تحديد اسم الصفحة إذا كانت محددة
    const issuePage = issuePageId ? availableSalesPages.find(p => p._id === issuePageId) : null;
    const issuePageName = issuePage?.name || null;

    setSavingIssue(true);
    try {
      // إصدار عمولة لكل شهر محدد
      const promises = selectedMonths.map(async (month) => {
        const net   = getReportNetSales(currentIssueBranch._id, month);
        const value = getEffectiveCommValue(currentIssueBranch._id, month);
        const monthStr = String(month).padStart(2, '0');
        const lastDay  = new Date(year, month, 0).getDate();

        await axios.post('/api/reports/commissions/issues', {
          branchId:        currentIssueBranch._id,
          branchName:      currentIssueBranch.name,
          invoicePageId:   issuePageId || null,
          invoicePageName: issuePageName,
          month,
          year,
          rate,
          value,
          netSales:   net,
          dateFrom:   `${year}-${monthStr}-01`,
          dateTo:     `${year}-${monthStr}-${String(lastDay).padStart(2, '0')}`,
          periodKey:  `${currentIssueBranch._id}_${year}_${month}${issuePageId ? `_${issuePageId}` : ''}`
        });
      });

      await Promise.all(promises);
      await fetchIssues();
      setIssueModalOpen(false);
      const pageLabel = issuePageName ? ` (${issuePageName})` : ' (إجمالي الفرع)';
      setSnackbar({
        open: true,
        message: `✅ تم إصدار عمولة ${currentIssueBranch.name}${pageLabel} لـ ${selectedMonths.length} شهر بإجمالي ${formatCurrency(totalVal)}`,
        severity: 'success'
      });
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'حدث خطأ أثناء إصدار العمولة', severity: 'error' });
    } finally {
      setSavingIssue(false);
    }
  };

  // ===== حذف العمولة =====
  const openDeleteModal = (issue: CommissionIssue) => {
    setDeletingIssue(issue);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deletingIssue?._id) return;
    setDeletingLoading(true);
    try {
      await axios.delete(`/api/reports/commissions/issues/${deletingIssue._id}`);
      await fetchIssues();
      setDeleteModalOpen(false);
      const pageLabel = deletingIssue.invoicePageName ? ` (${deletingIssue.invoicePageName})` : ' (إجمالي الفرع)';
      setSnackbar({
        open: true,
        message: `🗑️ تم حذف عمولة ${MONTHS_AR[deletingIssue.month - 1]} ${deletingIssue.year}${pageLabel}`,
        severity: 'success'
      });
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'حدث خطأ أثناء الحذف', severity: 'error' });
    } finally {
      setDeletingLoading(false);
    }
  };

  // ===== تبويب الأهداف - الجدول الشهري =====
  // مفتاح الجدول: branchId_pageId_month (pageId='branch' للهدف الإجمالي)
  const getGridKey = (branchId: string, month: number, pageId?: string | null): string =>
    `${branchId}_${pageId || 'branch'}_${month}`;

  // الصفحات الفعلية المستخدمة في وضع الأهداف
  const getActiveGoalPages = (): InvoicePage[] => {
    if (targetMode === 'branch') return [];
    if (selectedGoalPages.length > 0) {
      return availableSalesPages.filter(p => selectedGoalPages.includes(p._id));
    }
    return availableSalesPages;
  };

  const initEditGrid = () => {
    const grid: Record<string, string> = {};
    const activePages = getActiveGoalPages();
    const pagesToShow = activePages.length > 0 ? activePages : availableSalesPages;

    if (targetMode === 'branch') {
      // وضع الهدف الإجمالي للفرع: هدف مستقل لكل فرع
      branches.forEach(branch => {
        for (let m = 1; m <= 12; m++) {
          const t = getMonthlyTarget(branch._id, m, parseInt(targetsYear), null);
          grid[getGridKey(branch._id, m, null)] = t ? String(t.targetAmount) : '';
        }
      });
    } else {
      // وضع الصفحات: نهيئ قيمة هدف أول فرع فقط لكل صفحة+شهر
      const firstBranch = branches[0];
      if (firstBranch) {
        pagesToShow.forEach(page => {
          for (let m = 1; m <= 12; m++) {
            const t = getMonthlyTarget(firstBranch._id, m, parseInt(targetsYear), page._id);
            grid[getGridKey(firstBranch._id, m, page._id)] = t ? String(t.targetAmount) : '';
          }
        });
      }
    }
    setEditGrid(grid);
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditGrid({});
    setEditMode(false);
  };

  const saveAllTargets = async () => {
    const targetsToSave: any[] = [];
    const activePages = getActiveGoalPages();
    const pagesToShow = activePages.length > 0 ? activePages : availableSalesPages;

    if (targetMode === 'branch') {
      // وضع الفرع: هدف مستقل لكل فرع
      branches.forEach(branch => {
        for (let m = 1; m <= 12; m++) {
          const key = getGridKey(branch._id, m, null);
          const val = editGrid[key];
          if (val !== undefined && val !== '' && !isNaN(parseFloat(val)) && parseFloat(val) >= 0) {
            targetsToSave.push({
              branchId:        branch._id,
              year:            parseInt(targetsYear),
              month:           m,
              targetAmount:    parseFloat(val),
              invoicePageId:   null,
              invoicePageName: null
            });
          }
        }
      });
    } else {
      // وضع الصفحات: نحفظ الهدف لأول فرع فقط (هدف واحد للصفحة)
      const firstBranch = branches[0];
      if (firstBranch) {
        pagesToShow.forEach(page => {
          for (let m = 1; m <= 12; m++) {
            const firstKey = getGridKey(firstBranch._id, m, page._id);
            const val = editGrid[firstKey];
            if (val !== undefined && val !== '' && !isNaN(parseFloat(val)) && parseFloat(val) >= 0) {
              targetsToSave.push({
                branchId:        firstBranch._id,
                year:            parseInt(targetsYear),
                month:           m,
                targetAmount:    parseFloat(val),
                invoicePageId:   page._id,
                invoicePageName: page.name
              });
            }
          }
        });
      }
    }

    if (targetsToSave.length === 0) {
      setSnackbar({ open: true, message: 'لا توجد أهداف لحفظها', severity: 'error' });
      return;
    }

    setSavingTargets(true);
    try {
      const response = await axios.post('/api/reports/commissions/targets/bulk', {
        targets: targetsToSave
      });
      await fetchTargets();
      setEditMode(false);
      setEditGrid({});
      const errCount = response.data.errors || 0;
      setSnackbar({
        open: true,
        message: errCount > 0
          ? `✅ تم حفظ ${response.data.saved} هدف بنجاح، ⚠️ فشل ${errCount} هدف`
          : `✅ تم حفظ ${response.data.saved} هدف بنجاح`,
        severity: errCount > 0 ? 'error' : 'success'
      });
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'حدث خطأ أثناء الحفظ', severity: 'error' });
    } finally {
      setSavingTargets(false);
    }
  };

  // ===== حساب إجمالي الأهداف السنوية لكل فرع =====
  const getBranchYearlyTotal = (branchId: string): number => {
    let total = 0;
    for (let m = 1; m <= 12; m++) {
      total += getMonthlyTargetAmount(branchId, m, parseInt(targetsYear));
    }
    return total;
  };

  const getLastYearMonthSales = (branchId: string, month: number): number =>
    lastYearSales[branchId]?.[month] || 0;

  // مبيعات السنة الماضية لصفحة محددة لجميع الفروع في شهر معين
  const getLastYearPageMonthSales = (pageId: string, month: number): number =>
    branches.reduce((sum, b) => sum + (lastYearPageSales[b._id]?.[pageId]?.[month] || 0), 0);

  // إجمالي مبيعات السنة الماضية لصفحة محددة لجميع الفروع سنوياً
  const getLastYearPageYearTotal = (pageId: string): number => {
    let total = 0;
    for (let m = 1; m <= 12; m++) {
      total += getLastYearPageMonthSales(pageId, m);
    }
    return total;
  };

  const getBranchLastYearTotal = (branchId: string): number => {
    let total = 0;
    for (let m = 1; m <= 12; m++) {
      total += getLastYearMonthSales(branchId, m);
    }
    return total;
  };

  // ===== الفروع المعروضة في التقرير =====
  const displayedBranches = reportBranchId
    ? branches.filter(b => b._id === reportBranchId)
    : branches;

  // ===== الواجهة =====
  return (
    <Box sx={{ direction: 'rtl' }}>
      {/* ===== التبويبات ===== */}
      <Paper elevation={0} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 2 }}
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab
            icon={<AssessmentIcon />}
            iconPosition="start"
            label="تقرير العمولات"
            sx={{ fontWeight: 'bold', minHeight: 56 }}
          />
          <Tab
            icon={<TableChartIcon />}
            iconPosition="start"
            label="الأهداف الشهرية"
            sx={{ fontWeight: 'bold', minHeight: 56 }}
          />
        </Tabs>
      </Paper>

      {/* ===== تبويب 1: تقرير العمولات (جدول شهري) ===== */}
      <TabPanel value={activeTab} index={0}>
        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
          {/* رأس التبويب */}
          <Box sx={{
            display: 'flex', alignItems: 'center',
            p: 1.5, borderBottom: '1px solid', borderColor: 'divider',
            flexWrap: 'wrap', gap: 1.5
          }}>
            <Typography variant="subtitle2" fontWeight="bold" color="primary" sx={{ flexShrink: 0 }}>
              💰 جدول عمولات المبيعات الشهرية
            </Typography>
            {/* اختيار السنة */}
            <TextField
              label="السنة" select size="small" sx={{ minWidth: 100 }}
              value={reportYear}
              onChange={e => setReportYear(e.target.value)}
            >
              {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i).map(y => (
                <MenuItem key={y} value={String(y)}>{y}</MenuItem>
              ))}
            </TextField>
            {/* اختيار الفرع */}
            <TextField
              label="الفرع" select size="small" sx={{ minWidth: 160 }}
              value={reportBranchId}
              onChange={e => setReportBranchId(e.target.value)}
            >
              <MenuItem value="">جميع الفروع</MenuItem>
              {branches.map(b => <MenuItem key={b._id} value={b._id}>{b.name}</MenuItem>)}
            </TextField>
            {/* وضع العرض (مشترك مع تبويب الأهداف) */}
            <TextField
              select size="small" sx={{ minWidth: 200 }}
              label="وضع العرض"
              value={targetMode}
              onChange={e => {
                setTargetMode(e.target.value as 'branch' | 'page');
                setReportSalesData({});
                setReportPageSalesData({});
              }}
            >
              <MenuItem value="branch">🏢 عرض بالفرع</MenuItem>
              <MenuItem value="page">📄 عرض بالفرع + الصفحة</MenuItem>
            </TextField>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="contained" size="small"
              startIcon={loadingReport ? <CircularProgress size={14} color="inherit" /> : <SearchIcon />}
              onClick={fetchReportSalesData}
              disabled={loadingReport}
              sx={{ minWidth: 130, flexShrink: 0 }}
            >
              {loadingReport ? 'جاري التحميل...' : 'عرض التقرير'}
            </Button>
          </Box>

          {loadingReport && <LinearProgress />}

          {/* فلاتر صفحات الفواتير */}
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#f8f9fa' }}>
            <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
              📄 فلترة صفحات الفواتير (اختياري)
            </Typography>
            <Grid container spacing={2}>
              {/* صفحات المبيعات */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="report-sales-pages-label">
                    {selectedSalesPages.length === 0 ? 'صفحات المبيعات - الكل (افتراضي)' : `${selectedSalesPages.length} صفحة مبيعات محددة`}
                  </InputLabel>
                  <Select
                    labelId="report-sales-pages-label"
                    multiple
                    value={selectedSalesPages}
                    onChange={(e: SelectChangeEvent<string[]>) => {
                      const val = e.target.value;
                      setSelectedSalesPages(typeof val === 'string' ? val.split(',') : val);
                    }}
                    input={<OutlinedInput label={selectedSalesPages.length === 0 ? 'صفحات المبيعات - الكل (افتراضي)' : `${selectedSalesPages.length} صفحة مبيعات محددة`} />}
                    renderValue={(selected) => {
                      if (selected.length === 0) return 'الكل (افتراضي)';
                      return selected.map(id => availableSalesPages.find(p => p._id === id)?.name || id).join('، ');
                    }}
                    disabled={loadingPages}
                    MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                  >
                    {availableSalesPages.map(page => (
                      <MenuItem key={page._id} value={page._id} sx={{ py: 0.5 }}>
                        <Checkbox checked={selectedSalesPages.includes(page._id)} size="small" />
                        <ListItemText primary={page.name} primaryTypographyProps={{ fontSize: 13 }} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {selectedSalesPages.length > 0 && (
                  <Button size="small" onClick={() => setSelectedSalesPages([])}
                    sx={{ mt: 0.5, fontSize: 11, color: 'error.main', p: 0, minWidth: 'auto' }}>
                    ✕ إلغاء التحديد
                  </Button>
                )}
              </Grid>
              {/* صفحات المردودات */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="report-return-pages-label">
                    {selectedReturnPages.length === 0 ? 'صفحات المردودات - الكل (افتراضي)' : `${selectedReturnPages.length} صفحة مردودات محددة`}
                  </InputLabel>
                  <Select
                    labelId="report-return-pages-label"
                    multiple
                    value={selectedReturnPages}
                    onChange={(e: SelectChangeEvent<string[]>) => {
                      const val = e.target.value;
                      setSelectedReturnPages(typeof val === 'string' ? val.split(',') : val);
                    }}
                    input={<OutlinedInput label={selectedReturnPages.length === 0 ? 'صفحات المردودات - الكل (افتراضي)' : `${selectedReturnPages.length} صفحة مردودات محددة`} />}
                    renderValue={(selected) => {
                      if (selected.length === 0) return 'الكل (افتراضي)';
                      return selected.map(id => availableReturnPages.find(p => p._id === id)?.name || id).join('، ');
                    }}
                    disabled={loadingPages}
                    MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                  >
                    {availableReturnPages.map(page => (
                      <MenuItem key={page._id} value={page._id} sx={{ py: 0.5 }}>
                        <Checkbox checked={selectedReturnPages.includes(page._id)} size="small" />
                        <ListItemText primary={page.name} primaryTypographyProps={{ fontSize: 13 }} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {selectedReturnPages.length > 0 && (
                  <Button size="small" onClick={() => setSelectedReturnPages([])}
                    sx={{ mt: 0.5, fontSize: 11, color: 'error.main', p: 0, minWidth: 'auto' }}>
                    ✕ إلغاء التحديد
                  </Button>
                )}
              </Grid>
            </Grid>
          </Box>

          {/* رسالة الخطأ */}
          {error && <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>{error}</Alert>}

          {/* الجدول الشهري */}
          <TableContainer sx={{ overflowX: 'auto', mt: 1 }}>
            <Table sx={{ minWidth: 1800, tableLayout: 'fixed' }}>
              <TableHead>
                {/* صف العناوين الرئيسية */}
                <TableRow sx={{ bgcolor: '#0d2b45' }}>
                  <TableCell
                    align="center"
                    sx={{
                      color: '#fff', fontWeight: 'bold', border: '1px solid #1a3c5e',
                      fontSize: 14, py: 2, width: 160,
                      position: 'sticky', right: 0, zIndex: 3, bgcolor: '#0d2b45'
                    }}
                  >
                    الفرع
                  </TableCell>
                  {MONTHS_AR.map((month, i) => (
                    <TableCell key={i} align="center"
                      sx={{ color: '#fff', fontWeight: 'bold', border: '1px solid #1a3c5e', fontSize: 13, py: 2, width: 140 }}>
                      {month}
                    </TableCell>
                  ))}
                  <TableCell align="center"
                    sx={{ color: '#fff', fontWeight: 'bold', border: '1px solid #1a3c5e', fontSize: 13, py: 2, width: 150, bgcolor: '#1a5c3a' }}>
                    إجمالي السنة
                  </TableCell>
                  {canIssueCommission && (
                    <TableCell align="center"
                      sx={{ color: '#fff', fontWeight: 'bold', border: '1px solid #1a3c5e', fontSize: 13, py: 2, width: 130, bgcolor: '#1a3c5e' }}>
                      إصدار عمولة
                    </TableCell>
                  )}
                </TableRow>
                {/* صف فرعي */}
                <TableRow sx={{ bgcolor: '#1a3c5e' }}>
                  <TableCell
                    align="center"
                    sx={{
                      color: '#cce', fontSize: 11, border: '1px solid #2a5f8f', py: 1,
                      position: 'sticky', right: 0, zIndex: 3, bgcolor: '#1a3c5e', fontWeight: 'bold'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <HistoryIcon sx={{ fontSize: 13 }} />
                      <span>صافي المبيعات / الهدف</span>
                    </Box>
                  </TableCell>
                  {Array.from({ length: 12 }, (_, i) => (
                    <TableCell key={i} align="center"
                      sx={{ color: '#cce', fontSize: 11, border: '1px solid #2a5f8f', py: 1 }}>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#86efac', fontSize: 11, display: 'block', fontWeight: 'bold' }}>
                          صافي المبيعات
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#f0c040', fontSize: 11, display: 'block' }}>
                          الهدف
                        </Typography>
                      </Box>
                    </TableCell>
                  ))}
                  <TableCell align="center"
                    sx={{ color: '#cce', fontSize: 11, border: '1px solid #2a5f8f', py: 1, bgcolor: '#1a5c3a' }}>
                    <Typography variant="caption" sx={{ color: '#86efac', fontSize: 11, display: 'block', fontWeight: 'bold' }}>
                      الإجمالي
                    </Typography>
                  </TableCell>
                  {canIssueCommission && (
                    <TableCell align="center"
                      sx={{ color: '#cce', fontSize: 11, border: '1px solid #2a5f8f', py: 1, bgcolor: '#1a3c5e' }}>
                      <Typography variant="caption" sx={{ color: '#93c5fd', fontSize: 11, display: 'block' }}>
                        اختر الفرع
                      </Typography>
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {displayedBranches.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={15} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : targetMode === 'page' ? (
                  // ===== وضع الصفحات: صف لكل صفحة، الأعمدة = الأشهر =====
                  (() => {
                    const activePages = getActiveGoalPages();
                    const pagesToShow = activePages.length > 0 ? activePages : availableSalesPages;
                    if (pagesToShow.length === 0) {
                      return (
                        <TableRow>
                          <TableCell colSpan={15} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                            لا توجد صفحات مبيعات
                          </TableCell>
                        </TableRow>
                      );
                    }
                    return pagesToShow.map((page, pIdx) => {
                      const pageYearlyNet = Array.from({ length: 12 }, (_, m) => getPageTotalNetSales(page._id, m + 1)).reduce((a, b) => a + b, 0);
                      const pageYearlyTarget = Array.from({ length: 12 }, (_, m) => getPageTotalTarget(page._id, m + 1)).reduce((a, b) => a + b, 0);
                      return (
                        <TableRow key={page._id} sx={{ '&:hover': { bgcolor: '#e8f0fe' }, bgcolor: pIdx % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                          <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold', fontSize: 14, py: 1.5, px: 2, position: 'sticky', right: 0, zIndex: 1, bgcolor: pIdx % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                            📄 {page.name}
                          </TableCell>
                          {Array.from({ length: 12 }, (_, mIdx) => {
                            const month  = mIdx + 1;
                            const net    = getPageTotalNetSales(page._id, month);
                            const target = getPageTotalTarget(page._id, month);
                            const pct    = target > 0 ? (net / target) * 100 : null;
                            // جلب العمولات المُصدَرة لهذه الصفحة في هذا الشهر (لجميع الفروع المعروضة)
                            const pageIssuedList = displayedBranches.flatMap(b =>
                              issues.filter(i =>
                                (i.branchId === b._id || i.branchId?.toString() === b._id) &&
                                i.month === month &&
                                i.year === parseInt(reportYear) &&
                                (i.invoicePageId === page._id || i.invoicePageId?.toString() === page._id)
                              )
                            );
                            const hasPageIssued = pageIssuedList.length > 0;
                            return (
                              <TableCell key={month} align="center" sx={{ border: '1px solid #ddd', p: 1, bgcolor: hasPageIssued ? '#dbeafe' : target > 0 ? '#fef9c3' : 'inherit' }}>
                                <Box sx={{ minHeight: 56, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                  {Object.keys(reportSalesData).length > 0 ? (
                                    <Typography sx={{ fontSize: 14, fontWeight: 'bold', color: net >= 0 ? '#166534' : '#dc2626', display: 'block', lineHeight: 1.3 }}>{formatShort(net)}</Typography>
                                  ) : (
                                    <Typography sx={{ fontSize: 14, color: '#d1d5db' }}>—</Typography>
                                  )}
                                  {target > 0 && <Typography sx={{ fontSize: 13, fontWeight: 'bold', color: '#b45309', display: 'block', lineHeight: 1.3 }}>{formatShort(target)}</Typography>}
                                  {pct !== null && Object.keys(reportSalesData).length > 0 && (
                                    <Box sx={{ width: '100%' }}>
                                      <Typography sx={{ fontSize: 12, color: pct >= 100 ? '#166534' : pct >= 90 ? '#92400e' : '#dc2626', fontWeight: 'bold' }}>{pct.toFixed(0)}%</Typography>
                                      <LinearProgress variant="determinate" value={Math.min(pct, 100)} color={pct >= 100 ? 'success' : pct >= 90 ? 'warning' : 'error'} sx={{ height: 4, borderRadius: 1 }} />
                                    </Box>
                                  )}
                                  {pageIssuedList.map((issued, idx) => (
                                    <Box key={issued._id || idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mt: 0.3 }}>
                                      <Tooltip title={`${issued.branchName || 'فرع'}: ${formatCurrency(issued.value)} (${issued.rate}%)`}>
                                        <Chip
                                          label={`✔ ${issued.branchName || 'صدرت'}`}
                                          color="info" size="small"
                                          sx={{ fontSize: 10, height: 18, maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis' }}
                                        />
                                      </Tooltip>
                                      {canDeleteCommission && (
                                        <Tooltip title="حذف العمولة">
                                          <IconButton size="small" color="error" onClick={() => openDeleteModal(issued)} sx={{ p: 0.2, width: 18, height: 18 }}>
                                            <DeleteIcon sx={{ fontSize: 13 }} />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </Box>
                                  ))}
                                </Box>
                              </TableCell>
                            );
                          })}
                          <TableCell align="center" sx={{ border: '1px solid #ddd', bgcolor: '#dcfce7', py: 1.5 }}>
                            <Typography sx={{ fontSize: 15, fontWeight: 'bold', color: '#166534', display: 'block' }}>
                              {Object.keys(reportSalesData).length > 0 ? formatShort(pageYearlyNet) : '—'}
                            </Typography>
                            {pageYearlyTarget > 0 && (
                              <Typography sx={{ fontSize: 12, fontWeight: 'bold', color: '#b45309', display: 'block' }}>{formatShort(pageYearlyTarget)}</Typography>
                            )}
                          </TableCell>
                           {canIssueCommission && (
                             <TableCell align="center" sx={{ border: '1px solid #ddd', py: 1 }}>
                               {/* في وضع الصفحات، إصدار العمولة يكون للفروع */}
                               {displayedBranches.length === 1 ? (
                                 <Button variant="contained" color="success" size="small"
                                   startIcon={<CommissionIcon sx={{ fontSize: 14 }} />}
                                   onClick={() => openIssueModal(displayedBranches[0])}
                                   sx={{ fontSize: 11, py: 0.5, px: 1 }}>
                                   إصدار
                                 </Button>
                               ) : (
                                 <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                                   اختر فرعاً للإصدار
                                 </Typography>
                               )}
                             </TableCell>
                           )}
                        </TableRow>
                      );
                    });
                  })()
                ) : (
                  // ===== وضع الفرع: صف واحد لكل فرع =====
                  displayedBranches.map((branch, bIdx) => {
                    const yearlyNet = Object.values(reportSalesData[branch._id] || {}).reduce((s, v) => s + v, 0);
                    return (
                      <TableRow key={branch._id}
                        sx={{ '&:nth-of-type(even)': { bgcolor: '#f8f9fa' }, '&:hover': { bgcolor: '#e8f0fe' } }}>
                        <TableCell sx={{ border: '1px solid #ddd', fontWeight: 'bold', fontSize: 14, py: 1.5, px: 2, position: 'sticky', right: 0, zIndex: 1, bgcolor: bIdx % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                          {branch.name}
                        </TableCell>
                        {Array.from({ length: 12 }, (_, mIdx) => {
                          const month  = mIdx + 1;
                          const net    = getReportNetSales(branch._id, month);
                          // في وضع عرض الفرع: نعرض هدف الفرع الإجمالي فقط (invoicePageId = null)
                          const branchTargetObj = getMonthlyTarget(branch._id, month, parseInt(reportYear), null);
                          const target = branchTargetObj?.targetAmount || 0;
                          const allIssued = getAllIssuedRecords(branch._id, month, parseInt(reportYear));
                          const hasIssued = allIssued.length > 0;
                          const pct    = target > 0 ? (net / target) * 100 : null;
                          return (
                            <TableCell key={month} align="center" sx={{ border: '1px solid #ddd', p: 1, bgcolor: hasIssued ? '#dbeafe' : target > 0 ? '#fef9c3' : 'inherit' }}>
                              <Box sx={{ minHeight: 56, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                {Object.keys(reportSalesData).length > 0 ? (
                                  <Typography sx={{ fontSize: 14, fontWeight: 'bold', color: net >= 0 ? '#166534' : '#dc2626', display: 'block', lineHeight: 1.3 }}>{formatShort(net)}</Typography>
                                ) : (
                                  <Typography sx={{ fontSize: 14, color: '#d1d5db' }}>—</Typography>
                                )}
                                {target > 0 && <Typography sx={{ fontSize: 13, fontWeight: 'bold', color: '#b45309', display: 'block', lineHeight: 1.3 }}>{formatShort(target)}</Typography>}
                                {pct !== null && Object.keys(reportSalesData).length > 0 && (
                                  <Box sx={{ width: '100%' }}>
                                    <Typography sx={{ fontSize: 12, color: pct >= 100 ? '#166534' : pct >= 90 ? '#92400e' : '#dc2626', fontWeight: 'bold' }}>{pct.toFixed(0)}%</Typography>
                                    <LinearProgress variant="determinate" value={Math.min(pct, 100)} color={pct >= 100 ? 'success' : pct >= 90 ? 'warning' : 'error'} sx={{ height: 4, borderRadius: 1 }} />
                                  </Box>
                                )}
                                {allIssued.map((issued, idx) => (
                                  <Box key={issued._id || idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mt: 0.3 }}>
                                    <Tooltip title={`${issued.invoicePageName ? issued.invoicePageName + ': ' : 'إجمالي الفرع: '}${formatCurrency(issued.value)} (${issued.rate}%)`}>
                                      <Chip
                                        label={issued.invoicePageName ? `✔ ${issued.invoicePageName}` : '✔ إجمالي'}
                                        color="info" size="small"
                                        sx={{ fontSize: 10, height: 18, maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis' }}
                                      />
                                    </Tooltip>
                                    {canDeleteCommission && (
                                      <Tooltip title="حذف العمولة">
                                        <IconButton size="small" color="error" onClick={() => openDeleteModal(issued)} sx={{ p: 0.2, width: 18, height: 18 }}>
                                          <DeleteIcon sx={{ fontSize: 13 }} />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </Box>
                                ))}
                              </Box>
                            </TableCell>
                          );
                        })}
                        <TableCell align="center" sx={{ border: '1px solid #ddd', bgcolor: '#dcfce7', py: 1.5 }}>
                          <Typography sx={{ fontSize: 15, fontWeight: 'bold', color: '#166534', display: 'block' }}>
                            {Object.keys(reportSalesData).length > 0 ? formatShort(yearlyNet) : '—'}
                          </Typography>
                        </TableCell>
                        {canIssueCommission && (
                          <TableCell align="center" sx={{ border: '1px solid #ddd', py: 1.5 }}>
                            <Button variant="contained" color="success" size="small" startIcon={<CommissionIcon sx={{ fontSize: 14 }} />} onClick={() => openIssueModal(branch)} sx={{ fontSize: 11, py: 0.5, px: 1 }}>
                              إصدار
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                )}

                {/* صف الإجماليات */}
                {displayedBranches.length > 0 && Object.keys(reportSalesData).length > 0 && (
                  <TableRow sx={{ bgcolor: '#0d2b45' }}>
                    <TableCell
                      sx={{
                        color: '#fff', fontWeight: 'bold', border: '1px solid #1a3c5e', fontSize: 14, py: 1.5, px: 2,
                        position: 'sticky', right: 0, zIndex: 1, bgcolor: '#0d2b45'
                      }}
                    >
                      إجمالي الفروع
                    </TableCell>
                    {Array.from({ length: 12 }, (_, mIdx) => {
                      const month    = mIdx + 1;
                      const totalNet = displayedBranches.reduce((sum, b) => sum + getReportNetSales(b._id, month), 0);
                      return (
                        <TableCell key={month} align="center"
                          sx={{ border: '1px solid #1a3c5e', py: 1.5 }}>
                          <Typography sx={{ fontSize: 14, fontWeight: 'bold', color: totalNet > 0 ? '#86efac' : '#555', display: 'block' }}>
                            {totalNet !== 0 ? formatShort(totalNet) : '—'}
                          </Typography>
                        </TableCell>
                      );
                    })}
                    <TableCell align="center" sx={{ border: '1px solid #1a3c5e', bgcolor: '#1a5c3a', py: 1.5 }}>
                      <Typography sx={{ fontSize: 15, fontWeight: 'bold', color: '#86efac', display: 'block' }}>
                        {formatShort(displayedBranches.reduce((sum, b) =>
                          sum + Object.values(reportSalesData[b._id] || {}).reduce((s, v) => s + v, 0), 0))}
                      </Typography>
                    </TableCell>
                    {canIssueCommission && <TableCell sx={{ border: '1px solid #1a3c5e' }} />}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* مفتاح الألوان */}
          <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary" fontWeight="bold">مفتاح الألوان:</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 16, height: 16, bgcolor: '#fef9c3', border: '1px solid #eee', borderRadius: 0.5 }} />
              <Typography variant="caption" color="text.secondary">يوجد هدف محدد</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 16, height: 16, bgcolor: '#dbeafe', border: '1px solid #eee', borderRadius: 0.5 }} />
              <Typography variant="caption" color="text.secondary">صدرت العمولة</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 16, height: 16, bgcolor: '#fff', border: '1px solid #eee', borderRadius: 0.5 }} />
              <Typography variant="caption" color="text.secondary">لم يُحدد هدف</Typography>
            </Box>
          </Box>
        </Paper>
      </TabPanel>

      {/* ===== تبويب 2: الأهداف الشهرية ===== */}
      <TabPanel value={activeTab} index={1}>
        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
          {/* رأس التبويب */}
          <Box sx={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            p: 2, borderBottom: '1px solid', borderColor: 'divider',
            flexWrap: 'wrap', gap: 1
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary">
                🎯 جدول الأهداف الشهرية للفروع
              </Typography>
              <TextField
                label="السنة" select size="small" sx={{ minWidth: 100 }}
                value={targetsYear}
                onChange={e => setTargetsYear(e.target.value)}
              >
                {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i).map(y => (
                  <MenuItem key={y} value={String(y)}>{y}</MenuItem>
                ))}
              </TextField>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {editMode ? (
                <>
                  <Button
                    variant="outlined" color="error" size="small"
                    onClick={cancelEdit} disabled={savingTargets}
                  >
                    إلغاء
                  </Button>
                  <Button
                    variant="contained" color="success" size="small"
                    startIcon={savingTargets ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                    onClick={saveAllTargets} disabled={savingTargets}
                  >
                    {savingTargets ? 'جاري الحفظ...' : 'حفظ الأهداف'}
                  </Button>
                </>
              ) : (
                canEditTargets && (
                  <Button
                    variant="contained" color="warning" size="small"
                    startIcon={<EditIcon />}
                    onClick={initEditGrid}
                  >
                    تعديل الأهداف
                  </Button>
                )
              )}
            </Box>
          </Box>

          {(loadingTargets || loadingLastYear) && <LinearProgress />}

          {/* ===== إعدادات وضع الأهداف ===== */}
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#fff8e1' }}>
            <Typography variant="caption" color="warning.dark" fontWeight="bold" sx={{ mb: 1.5, display: 'block' }}>
              🎯 وضع تحديد الأهداف
            </Typography>
            <Grid container spacing={2} alignItems="flex-start">
              {/* اختيار الوضع */}
              <Grid item xs={12} sm={4}>
                <TextField
                  select fullWidth size="small"
                  label="وضع الأهداف"
                  value={targetMode}
                  onChange={e => {
                    setTargetMode(e.target.value as 'branch' | 'page');
                    if (editMode) cancelEdit();
                  }}
                >
                  <MenuItem value="branch">🏢 هدف إجمالي لكل فرع</MenuItem>
                  <MenuItem value="page">📄 هدف لكل فرع + صفحة مبيعات</MenuItem>
                </TextField>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {targetMode === 'branch'
                    ? 'هدف واحد لكل فرع بغض النظر عن صفحات المبيعات'
                    : 'هدف مستقل لكل فرع لكل صفحة مبيعات محددة'}
                </Typography>
              </Grid>

              {/* اختيار الصفحات (في وضع page فقط) */}
              {targetMode === 'page' && (
                <Grid item xs={12} sm={8}>
                  <Typography variant="caption" color="primary" sx={{ mb: 0.5, display: 'block', fontWeight: 'bold' }}>
                    📄 صفحات المبيعات المستهدفة
                    {selectedGoalPages.length > 0 && (
                      <Chip label={`${selectedGoalPages.length} محدد`} size="small" color="primary" sx={{ mr: 1, fontSize: 10, height: 18 }} />
                    )}
                  </Typography>
                  <FormControl fullWidth size="small">
                    <InputLabel id="goal-pages-label">
                      {selectedGoalPages.length === 0 ? 'جميع صفحات المبيعات (افتراضي)' : `${selectedGoalPages.length} صفحة محددة`}
                    </InputLabel>
                    <Select
                      labelId="goal-pages-label"
                      multiple
                      value={selectedGoalPages}
                      onChange={(e: SelectChangeEvent<string[]>) => {
                        const val = e.target.value;
                        setSelectedGoalPages(typeof val === 'string' ? val.split(',') : val);
                        if (editMode) cancelEdit();
                      }}
                      input={<OutlinedInput label={selectedGoalPages.length === 0 ? 'جميع صفحات المبيعات (افتراضي)' : `${selectedGoalPages.length} صفحة محددة`} />}
                      renderValue={(selected) => {
                        if (selected.length === 0) return 'الكل (افتراضي)';
                        return selected.map(id => availableSalesPages.find(p => p._id === id)?.name || id).join('، ');
                      }}
                      disabled={loadingPages}
                      MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                    >
                      {availableSalesPages.length === 0 ? (
                        <MenuItem disabled><em>{loadingPages ? 'جاري التحميل...' : 'لا توجد صفحات'}</em></MenuItem>
                      ) : (
                        availableSalesPages.map(page => (
                          <MenuItem key={page._id} value={page._id} sx={{ py: 0.5 }}>
                            <Checkbox checked={selectedGoalPages.includes(page._id)} size="small" />
                            <ListItemText primary={page.name} primaryTypographyProps={{ fontSize: 13 }} />
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                  {selectedGoalPages.length > 0 && (
                    <Button size="small" onClick={() => { setSelectedGoalPages([]); if (editMode) cancelEdit(); }}
                      sx={{ mt: 0.5, fontSize: 11, color: 'error.main', p: 0, minWidth: 'auto' }}>
                      ✕ إلغاء التحديد (عرض الكل)
                    </Button>
                  )}
                </Grid>
              )}
            </Grid>
          </Box>

          {/* فلاتر صفحات الفواتير لحساب مبيعات السنة الماضية */}
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#f8f9fa' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="caption" color="text.secondary" fontWeight="bold">
                📊 فلترة صفحات الفواتير لحساب مبيعات السنة الماضية ({parseInt(targetsYear) - 1}) كمرجع للأهداف
              </Typography>
              <Button
                variant="contained" color="primary" size="small"
                startIcon={loadingLastYear ? <CircularProgress size={14} color="inherit" /> : <SearchIcon />}
                onClick={fetchLastYearSales}
                disabled={loadingLastYear}
                sx={{ minWidth: 110, flexShrink: 0 }}
              >
                {loadingLastYear ? 'جاري...' : 'تحديث الأرقام'}
              </Button>
            </Box>
            <Grid container spacing={2}>
              {/* صفحات المبيعات */}
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="primary" sx={{ mb: 0.5, display: 'block', fontWeight: 'bold' }}>
                  📄 صفحات فواتير المبيعات
                  {selectedTargetSalesPages.length > 0 && (
                    <Chip label={`${selectedTargetSalesPages.length} محدد`} size="small" color="primary" sx={{ mr: 1, fontSize: 10, height: 18 }} />
                  )}
                </Typography>
                <FormControl fullWidth size="small">
                  <InputLabel id="target-sales-pages-label">
                    {selectedTargetSalesPages.length === 0 ? 'الكل (افتراضي)' : `${selectedTargetSalesPages.length} صفحة محددة`}
                  </InputLabel>
                  <Select
                    labelId="target-sales-pages-label"
                    multiple
                    value={selectedTargetSalesPages}
                    onChange={(e: SelectChangeEvent<string[]>) => {
                      const val = e.target.value;
                      setSelectedTargetSalesPages(typeof val === 'string' ? val.split(',') : val);
                    }}
                    input={<OutlinedInput label={selectedTargetSalesPages.length === 0 ? 'الكل (افتراضي)' : `${selectedTargetSalesPages.length} صفحة محددة`} />}
                    renderValue={(selected) => {
                      if (selected.length === 0) return 'الكل (افتراضي)';
                      return selected.map(id => availableSalesPages.find(p => p._id === id)?.name || id).join('، ');
                    }}
                    disabled={loadingPages}
                    MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                  >
                    {availableSalesPages.length === 0 ? (
                      <MenuItem disabled><em>{loadingPages ? 'جاري التحميل...' : 'لا توجد صفحات'}</em></MenuItem>
                    ) : (
                      availableSalesPages.map(page => (
                        <MenuItem key={page._id} value={page._id} sx={{ py: 0.5 }}>
                          <Checkbox checked={selectedTargetSalesPages.includes(page._id)} size="small" />
                          <ListItemText primary={page.name} primaryTypographyProps={{ fontSize: 13 }} />
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
                {selectedTargetSalesPages.length > 0 && (
                  <Button size="small" onClick={() => setSelectedTargetSalesPages([])}
                    sx={{ mt: 0.5, fontSize: 11, color: 'error.main', p: 0, minWidth: 'auto' }}>
                    ✕ إلغاء التحديد
                  </Button>
                )}
              </Grid>

              {/* صفحات المردودات */}
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="warning.dark" sx={{ mb: 0.5, display: 'block', fontWeight: 'bold' }}>
                  🔄 صفحات فواتير المردودات
                  {selectedTargetReturnPages.length > 0 && (
                    <Chip label={`${selectedTargetReturnPages.length} محدد`} size="small" color="warning" sx={{ mr: 1, fontSize: 10, height: 18 }} />
                  )}
                </Typography>
                <FormControl fullWidth size="small">
                  <InputLabel id="target-return-pages-label">
                    {selectedTargetReturnPages.length === 0 ? 'الكل (افتراضي)' : `${selectedTargetReturnPages.length} صفحة محددة`}
                  </InputLabel>
                  <Select
                    labelId="target-return-pages-label"
                    multiple
                    value={selectedTargetReturnPages}
                    onChange={(e: SelectChangeEvent<string[]>) => {
                      const val = e.target.value;
                      setSelectedTargetReturnPages(typeof val === 'string' ? val.split(',') : val);
                    }}
                    input={<OutlinedInput label={selectedTargetReturnPages.length === 0 ? 'الكل (افتراضي)' : `${selectedTargetReturnPages.length} صفحة محددة`} />}
                    renderValue={(selected) => {
                      if (selected.length === 0) return 'الكل (افتراضي)';
                      return selected.map(id => availableReturnPages.find(p => p._id === id)?.name || id).join('، ');
                    }}
                    disabled={loadingPages}
                    MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                  >
                    {availableReturnPages.length === 0 ? (
                      <MenuItem disabled><em>{loadingPages ? 'جاري التحميل...' : 'لا توجد صفحات'}</em></MenuItem>
                    ) : (
                      availableReturnPages.map(page => (
                        <MenuItem key={page._id} value={page._id} sx={{ py: 0.5 }}>
                          <Checkbox checked={selectedTargetReturnPages.includes(page._id)} size="small" />
                          <ListItemText primary={page.name} primaryTypographyProps={{ fontSize: 13 }} />
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
                {selectedTargetReturnPages.length > 0 && (
                  <Button size="small" onClick={() => setSelectedTargetReturnPages([])}
                    sx={{ mt: 0.5, fontSize: 11, color: 'error.main', p: 0, minWidth: 'auto' }}>
                    ✕ إلغاء التحديد
                  </Button>
                )}
              </Grid>
            </Grid>
          </Box>

          {/* تعليمات */}
          {editMode && (
            <Alert severity="info" sx={{ m: 2, mb: 0 }}>
              💡 أدخل قيمة الهدف لكل فرع وشهر، ثم اضغط "حفظ الأهداف". الخلايا الفارغة لن تُحفظ.
              الأرقام الرمادية الصغيرة هي مبيعات نفس الشهر من السنة الماضية ({parseInt(targetsYear) - 1}) كمرجع.
            </Alert>
          )}

          {/* الجدول */}
          <TableContainer sx={{ overflowX: 'auto', mt: 1 }}>
            <Table sx={{ minWidth: 1800, tableLayout: 'fixed' }}>
              <TableHead>
                {/* صف العناوين الرئيسية */}
                <TableRow sx={{ bgcolor: '#0d2b45' }}>
                  <TableCell
                    align="center"
                    sx={{
                      color: '#fff', fontWeight: 'bold', border: '1px solid #1a3c5e',
                      fontSize: 14, py: 2, width: 160,
                      position: 'sticky', right: 0, zIndex: 3, bgcolor: '#0d2b45'
                    }}
                  >
                    الفرع
                  </TableCell>
                  {MONTHS_AR.map((month, i) => (
                    <TableCell key={i} align="center"
                      sx={{ color: '#fff', fontWeight: 'bold', border: '1px solid #1a3c5e', fontSize: 13, py: 2, width: 130 }}>
                      {month}
                    </TableCell>
                  ))}
                  <TableCell align="center"
                    sx={{ color: '#fff', fontWeight: 'bold', border: '1px solid #1a3c5e', fontSize: 13, py: 2, width: 150, bgcolor: '#1a5c3a' }}>
                    الإجمالي السنوي
                  </TableCell>
                </TableRow>
                {/* صف فرعي يوضح تسمية الصفوف */}
                <TableRow sx={{ bgcolor: '#1a3c5e' }}>
                  <TableCell
                    align="center"
                    sx={{
                      color: '#cce', fontSize: 11, border: '1px solid #2a5f8f', py: 1,
                      position: 'sticky', right: 0, zIndex: 3, bgcolor: '#1a3c5e', fontWeight: 'bold'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <HistoryIcon sx={{ fontSize: 13 }} />
                      <span>الهدف / مبيعات {parseInt(targetsYear) - 1}</span>
                    </Box>
                  </TableCell>
                  {Array.from({ length: 12 }, (_, i) => (
                    <TableCell key={i} align="center"
                      sx={{ color: '#cce', fontSize: 11, border: '1px solid #2a5f8f', py: 1 }}>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#f0c040', fontSize: 11, display: 'block', fontWeight: 'bold' }}>
                          الهدف
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#99aacc', fontSize: 11, display: 'block' }}>
                          مبيعات {parseInt(targetsYear) - 1}
                        </Typography>
                      </Box>
                    </TableCell>
                  ))}
                  <TableCell align="center"
                    sx={{ color: '#cce', fontSize: 11, border: '1px solid #2a5f8f', py: 1, bgcolor: '#1a5c3a' }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: '#f0c040', fontSize: 11, display: 'block', fontWeight: 'bold' }}>
                        الهدف
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#99aacc', fontSize: 11, display: 'block' }}>
                        مبيعات {parseInt(targetsYear) - 1}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {branches.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={14} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : targetMode === 'page' ? (
                  // ===== وضع الصفحات: صف واحد لكل صفحة (مجموع جميع الفروع) =====
                  (() => {
                    const activePages = getActiveGoalPages();
                    const pagesToShow = activePages.length > 0 ? activePages : availableSalesPages;
                    if (pagesToShow.length === 0) {
                      return (
                        <TableRow>
                          <TableCell colSpan={14} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                            لا توجد صفحات مبيعات
                          </TableCell>
                        </TableRow>
                      );
                    }
                    const firstBranch = branches[0];
                    return pagesToShow.map((page, pIdx) => {
                      // هدف الصفحة = هدف أول فرع فقط (لا نجمع الفروع)
                      const pageYearlyTarget = firstBranch
                        ? Array.from({ length: 12 }, (_, m) =>
                            getMonthlyTarget(firstBranch._id, m + 1, parseInt(targetsYear), page._id)?.targetAmount || 0
                          ).reduce((a, b) => a + b, 0)
                        : 0;
                      // إجمالي مبيعات السنة الماضية لهذه الصفحة تحديداً
                      const pageLastYearTotal = getLastYearPageYearTotal(page._id);

                      return (
                        <TableRow key={page._id}
                          sx={{ '&:hover': { bgcolor: '#e8f0fe' }, bgcolor: pIdx % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                          {/* اسم الصفحة */}
                          <TableCell
                            sx={{
                              border: '1px solid #ddd', fontWeight: 'bold', fontSize: 14, py: 1.5, px: 2,
                              position: 'sticky', right: 0, zIndex: 1,
                              bgcolor: pIdx % 2 === 0 ? '#fff' : '#f8f9fa'
                            }}
                          >
                            📄 {page.name}
                          </TableCell>

                          {/* خلايا الشهور */}
                          {Array.from({ length: 12 }, (_, mIdx) => {
                            const month = mIdx + 1;
                            // هدف الصفحة = هدف أول فرع فقط (لا نجمع الفروع)
                            const totalTarget = firstBranch
                              ? (getMonthlyTarget(firstBranch._id, month, parseInt(targetsYear), page._id)?.targetAmount || 0)
                              : 0;
                            // مبيعات السنة الماضية لهذه الصفحة تحديداً في هذا الشهر
                            const totalLY = getLastYearPageMonthSales(page._id, month);
                            // مفتاح التعديل: نستخدم أول فرع لتخزين الهدف
                            const editKey = firstBranch ? getGridKey(firstBranch._id, month, page._id) : '';

                            return (
                              <TableCell key={month} align="center"
                                sx={{
                                  border: '1px solid #ddd',
                                  p: editMode ? 0.75 : 1.25,
                                  bgcolor: totalTarget > 0 ? '#fef9c3' : 'inherit'
                                }}>
                                {editMode ? (
                                  <Box>
                                    <TextField
                                      size="small" type="number"
                                      value={editGrid[editKey] ?? ''}
                                      onChange={e => setEditGrid(prev => ({ ...prev, [editKey]: e.target.value }))}
                                      inputProps={{ min: 0, step: 1000, style: { textAlign: 'center', fontSize: 13, padding: '6px 4px' } }}
                                      sx={{ width: '100%', '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                                      placeholder="0"
                                    />
                                    {totalLY > 0 && (
                                      <Tooltip title={`مبيعات ${MONTHS_AR[mIdx]} ${parseInt(targetsYear) - 1} (${page.name}): ${formatCurrency(totalLY)}`}>
                                        <Typography sx={{ fontSize: 12, color: '#6b7280', display: 'block', mt: 0.5, cursor: 'help' }}>
                                          📊 {formatShort(totalLY)}
                                        </Typography>
                                      </Tooltip>
                                    )}
                                  </Box>
                                ) : (
                                  <Box sx={{ minHeight: 40, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.3 }}>
                                    {totalTarget > 0 ? (
                                      <Typography sx={{ fontSize: 13, fontWeight: 'bold', color: '#b45309', display: 'block' }}>
                                        {formatShort(totalTarget)}
                                      </Typography>
                                    ) : (
                                      <Typography sx={{ fontSize: 13, color: '#d1d5db', display: 'block' }}>—</Typography>
                                    )}
                                    {totalLY > 0 && (
                                      <Tooltip title={`مبيعات ${MONTHS_AR[mIdx]} ${parseInt(targetsYear) - 1} (${page.name}): ${formatCurrency(totalLY)}`}>
                                        <Typography sx={{ fontSize: 12, color: '#6b7280', display: 'block', cursor: 'help', borderTop: '1px dashed #e5e7eb', pt: 0.3, mt: 0.3, width: '100%', textAlign: 'center' }}>
                                          📊 {formatShort(totalLY)}
                                        </Typography>
                                      </Tooltip>
                                    )}
                                  </Box>
                                )}
                              </TableCell>
                            );
                          })}

                          {/* الإجمالي السنوي للصفحة */}
                          <TableCell align="center" sx={{ border: '1px solid #ddd', bgcolor: '#dcfce7', py: 1.5 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 'bold', color: '#166534', display: 'block' }}>
                              {pageYearlyTarget > 0 ? formatShort(pageYearlyTarget) : '—'}
                            </Typography>
                            {pageLastYearTotal > 0 && (
                              <Tooltip title={`إجمالي مبيعات ${parseInt(targetsYear) - 1} (${page.name}): ${formatCurrency(pageLastYearTotal)}`}>
                                <Typography sx={{ fontSize: 12, color: '#6b7280', display: 'block', cursor: 'help', borderTop: '1px dashed #e5e7eb', pt: 0.3, mt: 0.3 }}>
                                  📊 {formatShort(pageLastYearTotal)}
                                </Typography>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    });
                  })()
                ) : (
                  // ===== وضع الهدف الإجمالي للفرع (صف واحد لكل فرع) =====
                  branches.map((branch, bIdx) => {
                    const yearlyTarget  = getBranchYearlyTotal(branch._id);
                    const lastYearTotal = getBranchLastYearTotal(branch._id);
                    const rowBg         = bIdx % 2 === 0 ? '#fff' : '#f8f9fa';

                    return (
                      <TableRow key={branch._id}
                        sx={{ '&:hover': { bgcolor: '#e8f0fe' } }}>
                        {/* اسم الفرع */}
                        <TableCell
                          sx={{
                            border: '1px solid #ddd', fontWeight: 'bold', fontSize: 14, py: 1.5, px: 2,
                            position: 'sticky', right: 0, zIndex: 1, bgcolor: rowBg
                          }}
                        >
                          {branch.name}
                        </TableCell>

                        {/* خلايا الشهور */}
                        {Array.from({ length: 12 }, (_, mIdx) => {
                          const month    = mIdx + 1;
                          const target   = getMonthlyTarget(branch._id, month, parseInt(targetsYear), null);
                          const key      = getGridKey(branch._id, month, null);
                          const issued   = getIssuedRecord(branch._id, month, parseInt(targetsYear));
                          const lyAmount = getLastYearMonthSales(branch._id, month);

                          return (
                            <TableCell key={month} align="center"
                              sx={{
                                border: '1px solid #ddd',
                                p: editMode ? 0.75 : 1.25,
                                bgcolor: issued ? '#dbeafe' : target ? '#fef9c3' : 'inherit'
                              }}>
                              {editMode ? (
                                <Box>
                                  <TextField
                                    size="small" type="number"
                                    value={editGrid[key] ?? ''}
                                    onChange={e => setEditGrid(prev => ({ ...prev, [key]: e.target.value }))}
                                    inputProps={{ min: 0, step: 1000, style: { textAlign: 'center', fontSize: 13, padding: '6px 4px' } }}
                                    sx={{ width: '100%', '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                                    placeholder="0"
                                  />
                                  {lyAmount > 0 && (
                                    <Tooltip title={`مبيعات ${MONTHS_AR[mIdx]} ${parseInt(targetsYear) - 1}: ${formatCurrency(lyAmount)}`}>
                                      <Typography sx={{ fontSize: 12, color: '#6b7280', display: 'block', mt: 0.5, cursor: 'help' }}>
                                        📊 {formatShort(lyAmount)}
                                      </Typography>
                                    </Tooltip>
                                  )}
                                </Box>
                              ) : (
                                <Box sx={{ minHeight: 40, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.3 }}>
                                  {target ? (
                                    <Typography sx={{ fontSize: 13, fontWeight: 'bold', color: '#b45309', display: 'block' }}>
                                      {formatShort(target.targetAmount)}
                                    </Typography>
                                  ) : (
                                    <Typography sx={{ fontSize: 13, color: '#d1d5db', display: 'block' }}>—</Typography>
                                  )}
                                  {lyAmount > 0 && (
                                    <Tooltip title={`مبيعات ${MONTHS_AR[mIdx]} ${parseInt(targetsYear) - 1}: ${formatCurrency(lyAmount)}`}>
                                      <Typography sx={{ fontSize: 12, color: '#6b7280', display: 'block', cursor: 'help', borderTop: '1px dashed #e5e7eb', pt: 0.3, mt: 0.3, width: '100%', textAlign: 'center' }}>
                                        📊 {formatShort(lyAmount)}
                                      </Typography>
                                    </Tooltip>
                                  )}
                                  {issued && (
                                    <Tooltip title={`عمولة مُصدَرة: ${formatCurrency(issued.value)} (${issued.rate}%)`}>
                                      <Chip label="✔ صدرت" color="info" size="small" sx={{ fontSize: 10, height: 18, mt: 0.3 }} />
                                    </Tooltip>
                                  )}
                                </Box>
                              )}
                            </TableCell>
                          );
                        })}

                        {/* الإجمالي السنوي */}
                        <TableCell align="center" sx={{ border: '1px solid #ddd', bgcolor: '#dcfce7', py: 1.5 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 'bold', color: '#166534', display: 'block' }}>
                            {yearlyTarget > 0 ? formatShort(yearlyTarget) : '—'}
                          </Typography>
                          {lastYearTotal > 0 && (
                            <Tooltip title={`إجمالي مبيعات ${parseInt(targetsYear) - 1}: ${formatCurrency(lastYearTotal)}`}>
                              <Typography sx={{ fontSize: 12, color: '#6b7280', display: 'block', cursor: 'help', borderTop: '1px dashed #e5e7eb', pt: 0.3, mt: 0.3 }}>
                                📊 {formatShort(lastYearTotal)}
                              </Typography>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}

                {/* صف الإجماليات الكلية */}
                {branches.length > 0 && !editMode && (
                  <TableRow sx={{ bgcolor: '#0d2b45' }}>
                    <TableCell
                      sx={{
                        color: '#fff', fontWeight: 'bold', border: '1px solid #1a3c5e', fontSize: 14, py: 1.5, px: 2,
                        position: 'sticky', right: 0, zIndex: 1, bgcolor: '#0d2b45'
                      }}
                    >
                      إجمالي الأهداف
                    </TableCell>
                    {Array.from({ length: 12 }, (_, mIdx) => {
                      const month       = mIdx + 1;
                      const totalTarget = branches.reduce((sum, b) => sum + getMonthlyTargetAmount(b._id, month, parseInt(targetsYear)), 0);
                      const totalLY     = branches.reduce((sum, b) => sum + getLastYearMonthSales(b._id, month), 0);
                      return (
                        <TableCell key={month} align="center"
                          sx={{ border: '1px solid #1a3c5e', py: 1.5 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 'bold', color: totalTarget > 0 ? '#f0c040' : '#555', display: 'block' }}>
                            {totalTarget > 0 ? formatShort(totalTarget) : '—'}
                          </Typography>
                          {totalLY > 0 && (
                            <Typography sx={{ color: '#99aacc', fontSize: 12, display: 'block', borderTop: '1px dashed #2a5f8f', pt: 0.3, mt: 0.3 }}>
                              📊 {formatShort(totalLY)}
                            </Typography>
                          )}
                        </TableCell>
                      );
                    })}
                    <TableCell align="center" sx={{ border: '1px solid #1a3c5e', bgcolor: '#1a5c3a', py: 1.5 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 'bold', color: '#86efac', display: 'block' }}>
                        {formatShort(branches.reduce((sum, b) => sum + getBranchYearlyTotal(b._id), 0))}
                      </Typography>
                      <Typography sx={{ color: '#99aacc', fontSize: 12, display: 'block', borderTop: '1px dashed #2a5f8f', pt: 0.3, mt: 0.3 }}>
                        📊 {formatShort(branches.reduce((sum, b) => sum + getBranchLastYearTotal(b._id), 0))}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* مفتاح الألوان */}
          <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary" fontWeight="bold">مفتاح الألوان:</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 16, height: 16, bgcolor: '#fff8e1', border: '1px solid #eee', borderRadius: 0.5 }} />
              <Typography variant="caption" color="text.secondary">يوجد هدف محدد</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 16, height: 16, bgcolor: '#e3f2fd', border: '1px solid #eee', borderRadius: 0.5 }} />
              <Typography variant="caption" color="text.secondary">صدرت العمولة</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 16, height: 16, bgcolor: '#fff', border: '1px solid #eee', borderRadius: 0.5 }} />
              <Typography variant="caption" color="text.secondary">لم يُحدد هدف</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" color="text.disabled">📊</Typography>
              <Typography variant="caption" color="text.secondary">مبيعات {parseInt(targetsYear) - 1} (استرشادي)</Typography>
            </Box>
          </Box>
        </Paper>
      </TabPanel>

      {/* ===== مودال إصدار العمولة (متعدد الأشهر) ===== */}
      <Dialog open={issueModalOpen} onClose={() => setIssueModalOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { direction: 'rtl' } }}>
        <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider', color: 'primary.main', fontWeight: 'bold' }}>
          💰 إصدار عمولة — {currentIssueBranch?.name}
          {issuePageId && (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ mr: 1, fontWeight: 'normal' }}>
              ({availableSalesPages.find(p => p._id === issuePageId)?.name || 'صفحة'})
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          {currentIssueBranch && (
            <>
              {/* نوع العمولة + نسبة العمولة */}
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                {/* اختيار نوع العمولة */}
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel>نوع العمولة</InputLabel>
                  <Select
                    value={issuePageId || ''}
                    label="نوع العمولة"
                    onChange={(e: SelectChangeEvent) => {
                      const val = e.target.value;
                      setIssuePageId(val === '' ? null : val);
                      setSelectedMonths([]);
                      setCustomCommValues({});
                    }}
                  >
                    <MenuItem value="">إجمالي الفرع (بدون صفحة)</MenuItem>
                    {availableSalesPages.map(p => (
                      <MenuItem key={p._id} value={p._id}>{p.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="نسبة العمولة (%)" type="number" size="small"
                  value={issueRate} onChange={e => handleRateChange(e.target.value)}
                  inputProps={{ min: 0, max: 100, step: 0.5 }}
                  sx={{ width: 180 }}
                />
                <Typography variant="caption" color="text.secondary">
                  💡 اختر نوع العمولة ثم حدد الأشهر. يمكنك إصدار عمولة إجمالية للفرع وعمولة لكل صفحة في نفس الشهر.
                </Typography>
              </Box>
              {/* تنبيه إذا كانت هناك عمولات مُصدَرة مسبقاً لهذا النوع */}
              {currentIssueBranch && (() => {
                const existingForType = issues.filter(i =>
                  (i.branchId === currentIssueBranch._id || i.branchId?.toString() === currentIssueBranch._id) &&
                  i.year === parseInt(reportYear) &&
                  (issuePageId === null ? !i.invoicePageId : (i.invoicePageId === issuePageId || i.invoicePageId?.toString() === issuePageId))
                );
                if (existingForType.length === 0) return null;
                const months = existingForType.map(i => MONTHS_AR[i.month - 1]).join('، ');
                return (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    ℹ️ تم إصدار عمولة {issuePageId ? `صفحة "${availableSalesPages.find(p => p._id === issuePageId)?.name}"` : 'إجمالي الفرع'} مسبقاً لأشهر: {months}. هذه الأشهر ستكون مغلقة.
                  </Alert>
                );
              })()}

              {/* جدول اختيار الأشهر */}
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#0d2b45' }}>
                      <TableCell align="center" sx={{ color: '#fff', fontWeight: 'bold', border: '1px solid #1a3c5e', width: 50 }}>
                        {currentIssueBranch && (() => {
                          // الأشهر المتاحة (لم تُصدَر عمولتها لهذا النوع)
                          const availableMonths = Array.from({ length: 12 }, (_, i) => i + 1).filter(m =>
                            !getIssuedRecord(currentIssueBranch._id, m, parseInt(reportYear), issuePageId)
                          );
                          const allAvailableSelected = availableMonths.length > 0 && availableMonths.every(m => selectedMonths.includes(m));
                          return (
                            <Checkbox
                              size="small"
                              sx={{ color: '#fff', '&.Mui-checked': { color: '#86efac' } }}
                              checked={allAvailableSelected}
                              indeterminate={selectedMonths.length > 0 && !allAvailableSelected}
                              onChange={() => {
                                if (allAvailableSelected) setSelectedMonths([]);
                                else setSelectedMonths(availableMonths);
                              }}
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell align="center" sx={{ color: '#fff', fontWeight: 'bold', border: '1px solid #1a3c5e' }}>الشهر</TableCell>
                      <TableCell align="center" sx={{ color: '#fff', fontWeight: 'bold', border: '1px solid #1a3c5e' }}>صافي المبيعات</TableCell>
                      <TableCell align="center" sx={{ color: '#fff', fontWeight: 'bold', border: '1px solid #1a3c5e' }}>الهدف</TableCell>
                      <TableCell align="center" sx={{ color: '#fff', fontWeight: 'bold', border: '1px solid #1a3c5e' }}>نسبة التحقيق</TableCell>
                      <TableCell align="center" sx={{ color: '#fff', fontWeight: 'bold', border: '1px solid #1a3c5e', minWidth: 160 }}>
                        قيمة العمولة
                        <Typography variant="caption" sx={{ display: 'block', color: '#93c5fd', fontSize: 10 }}>
                          (تلقائي — قابل للتعديل)
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ color: '#fff', fontWeight: 'bold', border: '1px solid #1a3c5e' }}>الحالة</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {MONTHS_AR.map((monthName, mIdx) => {
                      const month      = mIdx + 1;
                      const net        = getReportNetSales(currentIssueBranch._id, month);
                      // هدف الشهر: إذا كانت صفحة محددة نعرض هدفها، وإلا هدف الفرع الإجمالي
                      const target = issuePageId
                        ? (getMonthlyTarget(currentIssueBranch._id, month, parseInt(reportYear), issuePageId)?.targetAmount || 0)
                        : (getMonthlyTarget(currentIssueBranch._id, month, parseInt(reportYear), null)?.targetAmount || 0);
                      const pct        = target > 0 ? (net / target) * 100 : null;
                      // البحث عن عمولة مُصدَرة لنفس نوع الإصدار المحدد (فرع أو صفحة)
                      const issued     = getIssuedRecord(currentIssueBranch._id, month, parseInt(reportYear), issuePageId);
                      const autoVal    = calcAutoCommValue(currentIssueBranch._id, month, issueRate);
                      const isSelected = selectedMonths.includes(month);
                      // القيمة المعروضة في الحقل: مخصصة أو تلقائية
                      const displayVal = customCommValues[month] !== undefined
                        ? customCommValues[month]
                        : autoVal.toFixed(2);

                      return (
                        <TableRow key={month}
                          sx={{
                            bgcolor: issued ? '#dbeafe' : isSelected ? '#f0fdf4' : 'inherit',
                            '&:hover': { bgcolor: issued ? '#dbeafe' : '#f8f9fa' },
                            cursor: issued ? 'not-allowed' : 'pointer'
                          }}
                          onClick={() => { if (!issued) toggleMonth(month); }}
                        >
                          <TableCell align="center" sx={{ border: '1px solid #eee' }}>
                            <Checkbox
                              size="small"
                              checked={isSelected}
                              disabled={!!issued}
                              onChange={() => { if (!issued) toggleMonth(month); }}
                              onClick={e => e.stopPropagation()}
                            />
                          </TableCell>
                          <TableCell align="center" sx={{ border: '1px solid #eee', fontWeight: 'bold' }}>
                            {monthName} {reportYear}
                          </TableCell>
                          <TableCell align="center" sx={{ border: '1px solid #eee' }}>
                            {Object.keys(reportSalesData).length > 0 ? (
                              <Typography variant="body2" fontWeight="bold" color={net >= 0 ? 'success.main' : 'error.main'}>
                                {formatCurrency(net)}
                              </Typography>
                            ) : (
                              <Typography variant="caption" color="text.disabled">لم يُحمَّل</Typography>
                            )}
                          </TableCell>
                          <TableCell align="center" sx={{ border: '1px solid #eee' }}>
                            {target > 0 ? (
                              <Typography variant="body2" color="warning.dark">{formatCurrency(target)}</Typography>
                            ) : (
                              <Typography variant="caption" color="text.disabled">—</Typography>
                            )}
                          </TableCell>
                          <TableCell align="center" sx={{ border: '1px solid #eee' }}>
                            {pct !== null && Object.keys(reportSalesData).length > 0 ? (
                              <Box>
                                <Typography variant="body2" fontWeight="bold"
                                  color={pct >= 100 ? 'success.main' : pct >= 90 ? 'warning.main' : 'error.main'}>
                                  {pct.toFixed(1)}%
                                </Typography>
                                <LinearProgress
                                  variant="determinate"
                                  value={Math.min(pct, 100)}
                                  color={pct >= 100 ? 'success' : pct >= 90 ? 'warning' : 'error'}
                                  sx={{ height: 4, borderRadius: 2, mt: 0.5 }}
                                />
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.disabled">—</Typography>
                            )}
                          </TableCell>
                          {/* حقل قيمة العمولة — تلقائي وقابل للتعديل */}
                          <TableCell align="center" sx={{ border: '1px solid #eee' }} onClick={e => e.stopPropagation()}>
                            {isSelected ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.3 }}>
                                <TextField
                                  size="small"
                                  type="number"
                                  value={displayVal}
                                  onChange={e => {
                                    setCustomCommValues(prev => ({ ...prev, [month]: e.target.value }));
                                  }}
                                  inputProps={{ min: 0, step: 0.01, style: { textAlign: 'center', fontSize: 13, padding: '4px 6px' } }}
                                  sx={{ width: 130, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                                />
                                {/* زر إعادة الحساب التلقائي */}
                                {customCommValues[month] !== undefined && (
                                  <Button
                                    size="small"
                                    onClick={() => setCustomCommValues(prev => {
                                      const updated = { ...prev };
                                      delete updated[month];
                                      return updated;
                                    })}
                                    sx={{ fontSize: 10, p: 0, minWidth: 'auto', color: 'primary.main' }}
                                  >
                                    ↺ تلقائي
                                  </Button>
                                )}
                              </Box>
                            ) : issued ? (
                              <Typography variant="body2" color="info.main" fontWeight="bold">
                                {formatCurrency(issued.value)}
                              </Typography>
                            ) : (
                              <Typography variant="caption" color="text.disabled">—</Typography>
                            )}
                          </TableCell>
                          <TableCell align="center" sx={{ border: '1px solid #eee' }}>
                            {issued ? (
                              <Chip label="✔ صدرت" color="info" size="small" sx={{ fontSize: 11 }} />
                            ) : (
                              <Chip
                                label={isSelected ? '✓ محدد' : 'غير محدد'}
                                color={isSelected ? 'success' : 'default'}
                                size="small"
                                sx={{ fontSize: 11 }}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* ملخص الإصدار */}
              {selectedMonths.length > 0 && (
                <Box sx={{ background: 'linear-gradient(135deg, #1a3c5e, #2a5f8f)', color: '#fff', borderRadius: 2, p: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={3}>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>نوع العمولة</Typography>
                      <Typography variant="body1" fontWeight="bold" sx={{ fontSize: 13 }}>
                        {issuePageId ? (availableSalesPages.find(p => p._id === issuePageId)?.name || 'صفحة') : 'إجمالي الفرع'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>الأشهر المحددة</Typography>
                      <Typography variant="h6" fontWeight="bold">{selectedMonths.length} شهر</Typography>
                      <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        {selectedMonths.map(m => MONTHS_AR[m - 1]).join('، ')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>نسبة العمولة</Typography>
                      <Typography variant="h6" fontWeight="bold">{issueRate}%</Typography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>إجمالي العمولة</Typography>
                      <Typography variant="h5" fontWeight="bold" color="#86efac">
                        {formatCurrency(getTotalCommissionValue())}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {selectedMonths.length === 0 && (
                <Alert severity="warning">
                  ⚠️ يرجى تحديد شهر واحد على الأقل لإصدار العمولة
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button variant="outlined" onClick={() => setIssueModalOpen(false)} disabled={savingIssue}>إلغاء</Button>
          <Button
            variant="contained" color="success"
            onClick={confirmIssue}
            disabled={savingIssue || selectedMonths.length === 0}
            startIcon={savingIssue ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
          >
            {savingIssue ? 'جاري الإصدار...' : `تأكيد إصدار ${selectedMonths.length > 0 ? selectedMonths.length + ' شهر' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== مودال تأكيد حذف العمولة ===== */}
      <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { direction: 'rtl' } }}>
        <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider', color: 'error.main', fontWeight: 'bold' }}>
          🗑️ حذف العمولة
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {deletingIssue && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                هل أنت متأكد من حذف هذه العمولة؟ لا يمكن التراجع عن هذا الإجراء.
              </Alert>
              <Paper elevation={0} sx={{ p: 2, bgcolor: '#fef2f2', borderRadius: 2, border: '1px solid #fecaca' }}>
                {[
                  { label: 'الفرع',    value: deletingIssue.branchName || '—' },
                  { label: 'نوع العمولة', value: deletingIssue.invoicePageName || 'إجمالي الفرع' },
                  { label: 'الشهر',    value: `${MONTHS_AR[deletingIssue.month - 1]} ${deletingIssue.year}` },
                  { label: 'النسبة',   value: `${deletingIssue.rate}%` },
                  { label: 'القيمة',   value: formatCurrency(deletingIssue.value) },
                  { label: 'صافي المبيعات', value: formatCurrency(deletingIssue.netSales) }
                ].map((row, i) => (
                  <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: i < 5 ? '1px dashed #fecaca' : 'none' }}>
                    <Typography variant="body2" color="text.secondary">{row.label}</Typography>
                    <Typography variant="body2" fontWeight="bold" color="error.dark">{row.value}</Typography>
                  </Box>
                ))}
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button variant="outlined" onClick={() => setDeleteModalOpen(false)} disabled={deletingLoading}>إلغاء</Button>
          <Button
            variant="contained" color="error"
            onClick={confirmDelete}
            disabled={deletingLoading}
            startIcon={deletingLoading ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          >
            {deletingLoading ? 'جاري الحذف...' : 'تأكيد الحذف'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== إشعار النجاح/الخطأ ===== */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CommissionsReport;
